/**
 * Enhanced Search Types
 *
 * Type definitions for the enhanced search functionality including
 * request/response interfaces, enums, and helper types.
 */

// ================================
// ENUMS
// ================================

/** Available content types for filtering */
export enum ESearchContentType {
  LESSON = 'lesson',
  CARE_ARTICLE = 'careArticle',
  LEARN_ARTICLE = 'learnArticle',
  QUESTIONNAIRE = 'questionnaire',
  QUIZ = 'quiz',
  QUERY = 'query',
}

/** Available topics for filtering */
export enum ESearchTopic {
  ALL = 'all',
  SLEEP = 'sleep',
  NUTRITION = 'nutrition',
  BEHAVIOR = 'behavior',
  SAFETY = 'safety',
  COMMUNICATION = 'communication',
  MEDICATION = 'medication',
  ACTIVITIES = 'activities',
  HYGIENE = 'hygiene',
  MOBILITY = 'mobility',
  COGNITION = 'cognition',
  SOCIAL = 'social',
  EMOTIONAL = 'emotional',
}

/** Available disease stages for filtering */
export enum ESearchDiseaseStage {
  ALL = 'all',
  EARLY = 'early',
  MIDDLE = 'middle',
  LATE = 'late',
}

/** Available content formats for filtering */
export enum ESearchFormat {
  ALL = 'all',
  PRACTICE = 'practice',
  THEORY = 'theory',
  CHECKLIST = 'checklist',
  VIDEO = 'video',
  AUDIO = 'audio',
  INTERACTIVE = 'interactive',
}

/** How search terms were matched */
export enum ESearchMatchType {
  TITLE = 'title',
  CONTENT = 'content',
  BOTH = 'both',
}

// ================================
// REQUEST INTERFACES
// ================================

/** Enhanced search request parameters */
export interface EnhancedSearchRequest {
  /** Search query text (minimum 3 characters) */
  searchText: string

  /** Filter by content types */
  contentTypes?: ESearchContentType[]

  /** Filter by medical/care topics */
  topics?: ESearchTopic[]

  /** Filter by disease progression stages */
  diseaseStages?: ESearchDiseaseStage[]

  /** Filter by content format types */
  formats?: ESearchFormat[]

  /** Maximum number of results to return (1-100, default: 20) */
  limit?: number

  /** Number of results to skip for pagination (default: 0) */
  offset?: number

  /** Semantic similarity threshold (0-1, default: 0.7) */
  semanticThreshold?: number
}

// ================================
// RESPONSE INTERFACES
// ================================

/** Individual search result */
export interface SearchResult {
  /** Unique identifier of the content */
  id: number

  /** Title of the content */
  title: string

  /** Content text/description */
  content: string

  /** Type of content */
  contentType: ESearchContentType

  /** How the match was found */
  matchType: ESearchMatchType

  /** Relevance score (0-1, higher is more relevant) */
  relevanceScore: number

  /** Title with search terms highlighted using <mark> tags */
  titleMatch?: string

  /** Content snippet with search terms highlighted */
  contentMatch?: string

  /** Associated timeline ID if applicable */
  timelineId?: number

  /** Current status of the content */
  status?: string

  /** When the content was created */
  createdAt: string

  /** When the content was last updated */
  updatedAt: string

  /** Additional metadata about the content */
  metadata?: SearchResultMetadata
}

/** Metadata for search results */
export interface SearchResultMetadata {
  /** Disease stage this content is designed for */
  diseaseStage?: ESearchDiseaseStage

  /** Primary topic/category */
  topic?: ESearchTopic

  /** Content format type */
  format?: ESearchFormat

  /** Duration in seconds (for video/audio content) */
  duration?: number

  /** Whether content has audio components */
  hasAudio?: boolean

  /** Whether content has video components */
  hasVideo?: boolean

  /** Whether content is published and available */
  isPublished?: boolean

  /** Languages this content is available in */
  availableIn?: string[]
}

/** Complete search response */
export interface EnhancedSearchResponse {
  /** Array of search results ordered by relevance */
  results: SearchResult[]

  /** Total number of matching results */
  totalCount: number

  /** Number of results that matched in titles */
  titleMatches: number

  /** Number of results that matched in content */
  contentMatches: number

  /** Whether there are more results available */
  hasMoreResults: boolean

  /** Suggestions for refining the search */
  suggestions?: SearchSuggestions

  /** Information about the processed query */
  query: SearchQueryInfo
}

/** Search refinement suggestions */
export interface SearchSuggestions {
  /** Suggested content type filters */
  contentTypes?: ESearchContentType[]

  /** Suggested topic filters */
  topics?: ESearchTopic[]

  /** Suggested disease stage filters */
  diseaseStages?: ESearchDiseaseStage[]

  /** Suggested format filters */
  formats?: ESearchFormat[]
}

/** Information about the processed search query */
export interface SearchQueryInfo {
  /** Original search text as entered by user */
  original: string

  /** Processed query used for database search */
  processed: string

  /** Extracted keywords from the search text */
  keywords: string[]
}

// ================================
// SUGGESTIONS INTERFACE
// ================================

/** Filter option with value and display label */
export interface FilterOption {
  value: string
  label: string
}

/** Available search suggestions and filter options */
export interface SearchSuggestionsResponse {
  /** Available content type filters with labels */
  contentTypes: FilterOption[]

  /** Available topic filters with labels */
  topics: FilterOption[]

  /** Available disease stage filters with labels */
  diseaseStages: FilterOption[]

  /** Available format filters with labels */
  formats: FilterOption[]

  /** Popular search queries for inspiration */
  popularQueries: string[]
}

// ================================
// FILTER INTERFACES
// ================================

/** Active search filters */
export interface SearchFilters {
  contentTypes: ESearchContentType[]
  topics: ESearchTopic[]
  diseaseStages: ESearchDiseaseStage[]
  formats: ESearchFormat[]
}

/** Search filter state for UI components */
export interface SearchFilterState {
  /** Currently selected filters */
  active: SearchFilters

  /** Available filter options */
  available: SearchSuggestionsResponse

  /** Whether filters are being loaded */
  loading: boolean

  /** Any errors loading filter options */
  error?: string
}

// ================================
// UI STATE INTERFACES
// ================================

/** Search component state */
export interface SearchState {
  /** Current search query */
  query: string

  /** Active filters */
  filters: SearchFilters

  /** Current search results */
  results?: EnhancedSearchResponse

  /** Whether search is in progress */
  loading: boolean

  /** Any search errors */
  error?: string

  /** Current page for pagination */
  page: number

  /** Results per page */
  pageSize: number
}

/** Search result display preferences */
export interface SearchDisplayOptions {
  /** How to display result snippets */
  snippetLength: number

  /** Whether to show metadata */
  showMetadata: boolean

  /** Whether to highlight search terms */
  highlightTerms: boolean

  /** Grouping preference */
  groupBy?: 'contentType' | 'topic' | 'diseaseStage' | 'none'
}

// ================================
// UTILITY TYPES
// ================================

/** Pagination information */
export interface SearchPagination {
  page: number
  pageSize: number
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

/** Search analytics data */
export interface SearchAnalytics {
  /** Search query */
  query: string

  /** Number of results returned */
  resultCount: number

  /** Time taken to execute search (ms) */
  executionTime: number

  /** Filters applied */
  filtersUsed: Partial<SearchFilters>

  /** Timestamp of search */
  timestamp: string

  /** User ID who performed search */
  userId?: number
}

/** Search history entry */
export interface SearchHistoryEntry {
  id: string
  query: string
  filters: Partial<SearchFilters>
  resultCount: number
  timestamp: string
}

// ================================
// ERROR TYPES
// ================================

/** Search-specific error types */
export enum ESearchErrorType {
  VALIDATION = 'validation',
  NETWORK = 'network',
  SERVER = 'server',
  TIMEOUT = 'timeout',
  UNKNOWN = 'unknown',
}

/** Search error with additional context */
export interface SearchError {
  type: ESearchErrorType
  message: string
  details?: string
  query?: string
  timestamp: string
}
